import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, CheckCircle, Clock, Star, MessageCircle, ArrowRight, FileText, Shield, Award } from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' } }),
};

const JournalWritingPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Journal Writing Service for Students | Lab Reports & Research Journals | AGO Innovations',
    description:
      'Professional journal writing service for students in India. Academic journals, lab reports, research summaries, and case studies — written by experts. Fast delivery, plagiarism-free.',
    keywords:
      'journal writing service, academic journal writing, lab report writing, research journal help, journal writing for students, college journal writing India, journal writing service India',
    canonical: 'https://agoinnovations.in/journal-writing-service',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Journal Writing Service for Students',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description: 'Expert journal writing service for college students — academic journals, lab reports, and research summaries.',
      areaServed: 'IN',
      serviceType: 'Academic Writing',
    },
  });

  const services = [
    { icon: <BookOpen className="w-6 h-6" />, title: 'Academic Journals', desc: 'Peer-review style journals for engineering, medical, and science students.' },
    { icon: <FileText className="w-6 h-6" />, title: 'Lab Reports', desc: 'Detailed lab reports with observations, analysis, and conclusions — all formats.' },
    { icon: <Star className="w-6 h-6" />, title: 'Research Summaries', desc: 'Concise research article summaries and literature review write-ups.' },
    { icon: <Shield className="w-6 h-6" />, title: 'Case Studies', desc: 'In-depth case study write-ups for management, medical, and engineering students.' },
    { icon: <Award className="w-6 h-6" />, title: 'Field Visit Reports', desc: 'Field visit and industrial visit reports with proper format and observations.' },
    { icon: <Clock className="w-6 h-6" />, title: 'Internship Reports', desc: 'Professional internship completion reports for college submissions.' },
  ];

  const faqs = [
    { q: 'What types of journals do you write?', a: 'We write academic journals, lab reports, research summaries, case studies, field visit reports, and internship reports — covering engineering, medical, science, and management streams.' },
    { q: 'How quickly is a journal delivered?', a: 'Standard delivery is within 24–72 hours. Urgent requests can be handled faster. Deadlines are always honoured.' },
    { q: 'Are journals plagiarism-free?', a: 'Yes. Every journal is written from scratch based on your specific topic and guidelines. No copy-paste from existing sources.' },
    { q: 'How do I submit my journal requirement?', a: 'Visit our Learn & Earn portal, subscribe to a plan, and submit your requirement — topic, format, word count, and deadline.' },
    { q: 'Do you cover all engineering branches?', a: 'Yes. Computer Science, Mechanical, Civil, Electrical, Electronics, Chemical, and all other engineering disciplines are covered.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-teal-900 via-cyan-900 to-teal-800 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative z-10 max-w-4xl mx-auto">
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">📓 Student Services — Journal Writing</span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">Journal Writing Service<span className="block text-yellow-400">for Students</span></h1>
          <p className="text-teal-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">Lab reports, academic journals, research summaries, and case studies — written by expert academic writers. Fast delivery, zero plagiarism.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/learn/customer-work')} className="bg-yellow-400 hover:bg-yellow-300 text-teal-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2">Submit Requirement <ArrowRight className="w-5 h-5" /></motion.button>
            <a href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20journal%20writing%20help." target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"><MessageCircle className="w-5 h-5" /> WhatsApp Us</a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-teal-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Plagiarism-Free</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> 24–72 Hour Delivery</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> All Streams Covered</span>
          </div>
        </motion.div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Journal Writing Services We Offer</h2>
            <p className="text-gray-500 text-lg">From lab reports to research journals — every academic writing need covered.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center text-teal-600 mb-4">{svc.icon}</div>
                <h3 className="font-black text-gray-900 text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">How It Works</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Subscribe to a Plan', desc: 'Choose a subscription plan for journal and academic writing.' },
              { step: '02', title: 'Submit Your Requirement', desc: 'Share topic, format, word count, and deadline for your journal.' },
              { step: '03', title: 'Receive Your Journal', desc: 'Get a well-researched, properly formatted journal on time.' },
            ].map((s, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-teal-50 rounded-2xl p-8 text-center border border-teal-100 relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-teal-700 text-white text-xs font-black px-4 py-1.5 rounded-full">Step {s.step}</div>
                <h3 className="font-black text-gray-900 text-base mb-2 mt-3">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Journal Writing FAQs</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-br from-teal-800 to-cyan-900 text-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black mb-4">Need Journal Writing Help?</h2>
          <p className="text-teal-200 mb-8">Submit your requirement today — journal delivered before your deadline.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/learn/customer-work')} className="bg-yellow-400 hover:bg-yellow-300 text-teal-900 font-bold px-8 py-4 rounded-full transition-colors">Submit Requirement</button>
            <button onClick={() => navigate('/assignment-writing-service')} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-colors">Assignment Writing</button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default JournalWritingPage;
